import React, { useCallback, useState } from 'react';
import MediaSkeleton from '../LoadingStates/MediaSkeleton';
import FileTile from '../../Chat/ChatMessageContent/FileTile/FileTile';
import './PostMediaCollage.css';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2000;

const PostMediaCollage = ({ mediaFiles, onMediaClick, postId, isDarkMode, isVisible = true, style = {} }) => {
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  // retryAttempts tracks current attempt per index — changing it forces a fresh <img> via key
  const [retryAttempts, setRetryAttempts] = useState({});

  // Safari video thumbnail fix: Add media fragment to force first frame display
  const getVideoSrcWithThumbnail = (url) => {
    if (!url) return '';
    // Don't modify if already has fragment or query params that might conflict
    if (url.includes('#t=') || url.includes('?t=')) return url;
    return `${url}#t=0.001`;
  };

  // Build the fileInfo shape FileTile expects (snake_case → camelCase).
  const toFileInfo = (file) => ({
    url: file.url,
    filename: file.original_filename,
    mimeType: file.mime_type,
    fileSize: file.file_size,
  });

  const getMediaLayout = (mediaFiles) => {
    const count = mediaFiles.length;

    switch (count) {
      case 1:
        return { type: 'single', aspectRatio: '16/10' };
      case 2:
        return { type: 'dual', aspectRatio: '2/1' };
      case 3:
        return { type: 'triple', aspectRatio: '16/10' };
      case 4:
        return { type: 'quad', aspectRatio: '1/1' };
      default:
        return { type: 'gallery', aspectRatio: '1/1' };
    }
  };

  const getGridArea = (layoutType, index) => {
    switch (layoutType) {
      case 'triple':
        if (index === 0) return '1 / 1 / 3 / 2'; // Large image spans 2 rows
        if (index === 1) return '1 / 2 / 2 / 3'; // Top right
        if (index === 2) return '2 / 2 / 3 / 3'; // Bottom right
        break;
      default:
        return 'auto';
    }
  };

  // Provide stable aspect ratios that work with grid layout
  const getItemAspectRatio = (layout, index) => {
    switch (layout.type) {
      case 'single': return '16/10';
      case 'dual': return '1/1';
      case 'triple': return 'auto'; // Let CSS Grid handle sizing to prevent conflicts
      case 'quad': return '1/1';
      case 'gallery': return '1/1';
      default: return '1/1';
    }
  };

  const handleImageLoad = useCallback((index) => {
    setLoadedImages(prev => new Set([...prev, index]));
    setFailedImages(prev => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  }, []);

  const handleImageError = useCallback((index) => {
    const currentAttempt = retryAttempts[index] || 0;

    if (currentAttempt < MAX_RETRIES) {
      // Schedule a retry — increment attempt count which changes the img key,
      // forcing React to mount a fresh <img> element that re-fetches the URL
      setTimeout(() => {
        setRetryAttempts(prev => ({ ...prev, [index]: (prev[index] || 0) + 1 }));
        // Reset loaded state so skeleton reappears during retry
        setLoadedImages(prev => {
          if (!prev.has(index)) return prev;
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, RETRY_DELAY_MS);
    } else {
      // Max retries exhausted — mark as failed (user can click to retry)
      setFailedImages(prev => new Set([...prev, index]));
      // Mark as "loaded" to hide the skeleton
      setLoadedImages(prev => new Set([...prev, index]));
    }
  }, [retryAttempts]);

  const handleManualRetry = useCallback((e, index) => {
    e.stopPropagation(); // Don't trigger media viewer
    // Reset state for this index to trigger a fresh load
    setRetryAttempts(prev => ({ ...prev, [index]: 0 }));
    setFailedImages(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    setLoadedImages(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  }, []);

  const handleMediaClick = useCallback((index) => {
    onMediaClick(index);
  }, [onMediaClick]);

  const renderMediaItem = (file, index) => {
    const layout = getMediaLayout(mediaFiles);
    const isLoaded = loadedImages.has(index);
    const isFailed = failedImages.has(index);
    const attempt = retryAttempts[index] || 0;
    const showOverlay = layout.type === 'gallery' && index === 3 && mediaFiles.length > 4;
    const itemAspectRatio = getItemAspectRatio(layout, index);

    // Build style object conditionally to avoid conflicts
    const itemStyle = {
      gridArea: getGridArea(layout.type, index)
    };

    // Only add aspect ratio if it's not 'auto' (avoids grid conflicts)
    if (itemAspectRatio !== 'auto') {
      itemStyle.aspectRatio = itemAspectRatio;
    }

    return (
      <div
        key={`${postId}-${index}`}
        className={`media-item ${layout.type === 'triple' && index === 0 ? 'large' : ''}`}
        onClick={() => handleMediaClick(index)}
        style={itemStyle}
      >
        {!isLoaded && (
          <MediaSkeleton
            isDarkMode={isDarkMode}
            layoutType={layout.type}
            itemIndex={index}
          />
        )}

        {file.file_type === 'image' ? (
          isFailed ? (
            <div
              className="media-error-placeholder"
              onClick={(e) => handleManualRetry(e, index)}
              title="Click to retry"
            >
              <span className="media-error-icon">🖼️</span>
              <span className="media-retry-label">Tap to retry</span>
            </div>
          ) : isVisible ? (
            <img
              key={`${postId}-img-${index}-attempt-${attempt}`}
              src={file.url}
              alt={file.original_filename}
              onLoad={() => handleImageLoad(index)}
              onError={() => handleImageError(index)}
              style={{ opacity: isLoaded ? 1 : 0, pointerEvents: 'none' }}
              loading="lazy"
              width="100%"
              height="100%"
            />
          ) : null
        ) : file.file_type === 'video' ? (
          isVisible ? (
            <video
              src={getVideoSrcWithThumbnail(file.url)}
              poster={file.thumbnail_url}
              onLoadedData={() => handleImageLoad(index)}
              style={{ opacity: isLoaded ? 1 : 0, pointerEvents: 'none' }}
              preload="metadata"
              playsInline
              width="100%"
              height="100%"
            />
          ) : null
        ) : (
          // Any non-image, non-video file (document OR audio) → FileTile.
          // clickMode="parent" lets the parent .media-item keep handling clicks.
          <FileTile
            fileInfo={toFileInfo(file)}
            mediaId={`${postId}-${index}`}
            messageIndex={0}
            mediaIndex={index}
            clickMode="parent"
          />
        )}

        {showOverlay && (
          <div className="media-overlay" style={{ pointerEvents: 'none' }}>
            <span>+{mediaFiles.length - 3}</span>
          </div>
        )}

        {file.file_type === 'video' && isLoaded && (
          <div className="video-indicator" style={{ pointerEvents: 'none' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  if (!mediaFiles || mediaFiles.length === 0) {
    return null;
  }

  const layout = getMediaLayout(mediaFiles);
  const displayFiles = layout.type === 'gallery' ? mediaFiles.slice(0, 4) : mediaFiles;

  return (
    <div
      className={`media-collage ${layout.type} ${isDarkMode ? 'dark' : 'light'}`}
      style={{
        aspectRatio: layout.aspectRatio,
        ...style
      }}
    >
      {displayFiles.map((file, index) => renderMediaItem(file, index))}
    </div>
  );
};

export default PostMediaCollage;
